<template>
  <v-app
    ref="vApp"
    v-resize="onResize"
    :dark="$ftAppIsDark"
    :class="{'ft-condense': condense, 'ft-navigation-drawer': true}"
    :style="coreCssVars"
  >
    <v-navigation-drawer
      v-model="navigationDrawerState"
      app
      :hide-overlay="true"
      clipped
      :disable-route-watcher="true"
      :disable-resize-watcher="true"
      :mobile-breakpoint="mobileBreakPoint"
      :width="menuWidth"
      :persistent="!mobileMenu"
      fixed
      :touchless="!mobileMenu"
    >
      <v-list
        expand
        class="subtitle-2"
      >
        <v-subheader v-if="mobileMenu">
          <v-btn
            icon
            @click="closeDrawer()"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-subheader>
        <template v-for="(item, i2) in availableRootMenuItems">
          <!-- Menu item with sub-menu items -->
          <v-list-group
            v-if="item.hasSub"
            v-show="mobileMenu || item.activePath || item.activeChildPath"
            :key="item.title + '_' + i2"
            :ref="'mgroup_' + i2"
            :value="item.active"
            :group="item.to"
            :ripple="false"
            @click="navGroupClick('mgroup_' + i2, item, $event)"
          >
            <template v-slot:activator>
              <!-- Root menu option -->
              <v-list-item-icon>
                <!-- if mobile menu, show notice next to icon -->
                <template v-if="mobileMenu && item.notice && item.notice[0]">
                  <v-badge
                    v-for="(notice, i) in item.notice.filter((n, i) => i < 1)"
                    :key="i"
                    left
                    :color="notice.color"
                  >
                    <template
                      v-slot:badge
                      right
                    >
                      <v-tooltip>
                        <template v-slot:activator>
                          <v-icon
                            v-if="notice.icon"
                            small
                            dark
                          >
                            {{ notice.icon }}
                          </v-icon>
                          <span v-else>{{ notice.text }}</span>
                        </template>
                        <span>{{ notice.message }}</span>
                      </v-tooltip>
                    </template>
                    <v-icon>
                      {{ item.icon || 'mdi-image-broken' }}
                    </v-icon>
                  </v-badge>
                </template>
                <!-- else just show icon -->
                <template v-else>
                  <v-icon
                    @click.native="routeIfActive(item, $event)"
                  >
                    {{ item.icon || 'mdi-image-broken' }}
                  </v-icon>
                </template>
              </v-list-item-icon>
              <v-list-item-title>
                {{ item.title }}
              </v-list-item-title>
            </template>
            <!-- Sub menu options -->
            <v-list-item
              v-for="subItem in item.items.filter(sm => sm.show)"
              :key="subItem.title"
              :ripple="false"
              :to="subItem.to"
              class="px-0"
              max-width="100%"
              @click="menuItemClickAction(subItem, true)"
            >
              <template v-if="subItem.notice && subItem.notice[0]">
                <v-badge
                  v-for="(notice, i) in subItem.notice.filter((n, i) => i < 1)"
                  :key="i"
                  left
                  :color="notice.color"
                  class="badge-nav-item"
                >
                  <v-tooltip
                    slot="badge"
                    right
                  >
                    <template slot="activator">
                      <v-icon
                        v-if="notice.icon"
                        small
                        dark
                      >
                        {{ notice.icon }}
                      </v-icon>
                      <span v-else>{{ notice.text }}</span>
                    </template>
                    <span>{{ notice.message }}</span>
                  </v-tooltip>
                  <v-list-item-title
                    class="body-2"
                  >
                    {{ subItem.title }}
                  </v-list-item-title>
                </v-badge>
              </template>
              <template v-else-if="subItem.hasSub">
                <!-- Menu subItem with sub-menu items -->
                <v-list-group
                  v-if="subItem.hasSub"
                  v-show="mobileMenu || subItem.activePath || subItem.activeChildPath || true"
                  :key="subItem.title + '_' + i2"
                  :ref="'mgroup_' + i2"
                  :value="subItem.active"
                  :group="subItem.to"
                  style="width: 100%;"
                >
                  <template v-slot:activator>
                    <v-list-item-title>
                      {{ subItem.title }}
                    </v-list-item-title>
                  </template>
                  <!-- Sub menu options -->
                  <v-list-item
                    v-for="subSubItem in subItem.items.filter(sm => sm.show)"
                    :key="subSubItem.title"
                    ripple
                    :to="subSubItem.to"
                    class="pl-8"
                    @click="menuItemClickAction(subSubItem, true)"
                  >
                    <v-list-item-title
                      class="body-2 font-weight-regular"
                    >
                      {{ subSubItem.title }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list-group>
              </template>
              <v-list-item-title
                v-else
                class="pl-4 body-2 font-weight-regular"
              >
                {{ subItem.title }}
              </v-list-item-title>
            </v-list-item>
          </v-list-group>
          <!-- Menu item, without sub-menu items -->
          <v-list-item
            v-else
            v-show="mobileMenu || item.activePath || item.activeChildPath"
            :key="item.title + '_' + i2"
            :to="item.to"
            :exact="item.exact"
            @click="closeDrawer()"
          >
            <v-list-item-icon>
              <!-- if mobile menu, show notice next to icon -->
              <template v-if="mobileMenu && item.notice && item.notice[0]">
                <v-badge
                  v-for="(notice, i) in item.notice.filter((n, i) => i < 1)"
                  :key="i"
                  left
                  :color="notice.color"
                >
                  <template
                    v-slot:badge
                    right
                  >
                    <v-tooltip>
                      <template v-slot:activator>
                        <v-icon
                          v-if="notice.icon"
                          small
                          dark
                        >
                          {{ notice.icon }}
                        </v-icon>
                        <span v-else>{{ notice.text }}</span>
                      </template>
                      <span>{{ notice.message }}</span>
                    </v-tooltip>
                  </template>
                  <v-icon>{{ item.icon || 'mdi-image-broken' }}</v-icon>
                </v-badge>
              </template>
              <!-- else just show icon -->
              <template v-else>
                <v-icon>{{ item.icon || 'mdi-image-broken' }}</v-icon>
              </template>
            </v-list-item-icon>
            <v-list-item-title>{{ item.title }}</v-list-item-title>
          </v-list-item>
        </template>
      </v-list>
    </v-navigation-drawer>

    <!-- <v-app-bar app clipped-left scroll-target="#ftMainContent"> -->
    <v-app-bar
      app
      clipped-left
    >
      <!-- <v-toolbar fixed> -->
      <template v-if="mobileMenu && firstMenuNotice">
        <v-badge
          v-for="(notice, i) in firstMenuNotice.notice"
          :key="i"
          class="ft-mobile-menu-badge"
          overlap
          :color="notice.color"
        >
          <v-tooltip
            slot="badge"
            bottom
          >
            <template slot="activator">
              <v-icon
                v-if="notice.icon"
                small
                dark
              >
                {{ notice.icon }}
              </v-icon>
              <span
                v-else
                class="ft-badge-text"
              >{{ notice.text }}</span>
            </template>
            <span>{{ notice.message }}</span>
          </v-tooltip>
          <v-app-bar-nav-icon @click="toggleDrawer" />
          <!-- <v-toolbar-side-icon @click="toggleDrawer"></v-toolbar-side-icon> -->
        </v-badge>
      </template>
      <!-- <v-toolbar-side-icon v-else-if="mobileMenu" @click="toggleDrawer"></v-toolbar-side-icon> -->
      <v-app-bar-nav-icon
        v-else-if="mobileMenu"
        @click="toggleDrawer"
      />
      <div style="height: 100%; width: 110px; position relative">
        <router-link
          class="ft-logo"
          to="/"
          style="display: block; height: 80%; margin: 0; position: absolute; top: 50%; transform: translate(0%, -50%)"
        >
          <img
            :src="headerLogo"
            :alt="appName"
            style="display: block; height: 100%"
            :class="{'ft-invert': invertLogo}"
          >
        </router-link>
      </div>
      <v-spacer />
      <v-tabs
        v-if="!mobileMenu && userLoggedIn"
        icons-and-text
        centered
        class="ft-nav-tabs"
        :mobile-breakpoint="0"
      >
        <v-tabs-slider :color="$ftStyle.mainTabsSlider" />
        <v-tab
          v-for="(item, i) in availableRootMenuItems"
          :key="i"
          :to="item.to"
          :exact="item.exact"
        >
          {{ item.title }}
          <template v-if="item.notice && item.notice[0]">
            <v-badge
              v-for="(notice, i2) in item.notice.filter((n, i) => i < 1)"
              :key="i2"
              overlap
              :color="notice.color"
            >
              <v-tooltip
                slot="badge"
                bottom
              >
                <template slot="activator">
                  <v-icon
                    v-if="notice.icon"
                    small
                    dark
                  >
                    {{ notice.icon }}
                  </v-icon>
                  <span
                    v-else
                    class="ft-badge-text"
                  >{{ notice.text }}</span>
                </template>
                <span>{{ notice.message }}</span>
              </v-tooltip>
              <v-icon>{{ item.icon }}</v-icon>
            </v-badge>
          </template>
          <v-icon v-else>
            {{ item.icon }}
          </v-icon>
        </v-tab>
      </v-tabs>
      <v-spacer />
      <slot name="navbar-buttons" />
      <v-btn
        v-if="$ftAC.lockScreen"
        icon
        @click="lockScreen"
      >
        <v-icon color="grey darken-1">
          mdi-monitor-lock
        </v-icon>
      </v-btn>
      <v-btn
        v-if="$ftAC.fastUserSwitch && !allowAddNewTerminal"
        icon
        @click="userSelect"
      >
        <v-icon color="grey darken-1">
          mdi-account-switch-outline
        </v-icon>
      </v-btn>
      <v-menu offset-y>
        <template v-slot:activator="{ on }">
          <v-badge
            class="ft-notify"
            overlap
            :color="notificationColor"
            :value="hasNotifications"
          >
            <v-icon
              slot="badge"
              dark
              v-on="on"
            >
              {{ notificationIcon }}
            </v-icon>
            <v-btn
              icon
              v-on="on"
            >
              <v-avatar v-if="accountMenuAvatar">
                <img :src="accountMenuAvatar">
              </v-avatar>
              <v-icon
                v-else
                color="grey darken-1"
              >
                {{ accountMenuIcon }}
              </v-icon>
            </v-btn>
          </v-badge>
        </template>
        <v-list>
          <slot name="account-menu-user">
            <v-list-item inactive>
              <v-list-item-title class="title">
                {{ $ftUser.name }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item
              v-if="this.$ftAC.terminal && $ftAC.terminal.id"
              inactive
              style="min-height: 0"
            >
              <v-list-item-title
                class="subtitle-2 pb-1"
              >
                {{ $ftAC.terminal.name }} - {{ $ftAC.terminal.groupName }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item
              inactive
              style="min-height: 0"
            >
              <v-list-item-title
                class="subtitle-2 pb-1"
              >
                {{ $ftAC.clubFullName }}
              </v-list-item-title>
            </v-list-item>
          </slot>
          <v-list-item
            v-for="(link, i) in externalLinks"
            :id="link.id"
            :key="i"
            @click="externalLink(link)"
          >
            <v-list-item-action>
              <v-icon>{{ link.icon }}</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>{{ link.text }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            v-if="allowAddNewTerminal"
            @click="addNewTerminal()"
          >
            <v-list-item-action>
              <v-icon>mdi-lan</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>Add Terminal to Group</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            v-if="allowAppSettingsOption"
            @click="showAppSettings = true"
          >
            <v-list-item-action>
              <v-icon>mdi-cog-box</v-icon>
            </v-list-item-action>
            <v-list-item-content>
              <v-list-item-title>App Settings</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <slot name="account-menu-help">
            <v-list-item @click="showHelp = true">
              <v-list-item-action>
                <v-icon>fti-logo</v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>About</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </slot>
          <slot name="account-menu-options" />
          <slot name="account-menu-clock-out">
            <v-list-item
              v-if="showClockOut"
              key="clockOut"
              @click="clockOut"
            >
              <v-list-item-action>
                <v-icon>{{ clockOutIcon }}</v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{ clockOutText }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </slot>
          <slot name="account-menu-logout">
            <v-list-item
              key="logout"
              @click="logout"
            >
              <v-list-item-action>
                <v-icon>{{ logoutIcon }}</v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>{{ logoutText }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </slot>
          <slot name="account-menu-update">
            <v-list-item
              v-if="$ftAS.updateAvailable"
              @click="showAppUpdateMessage"
            >
              <v-list-item-action>
                <v-icon color="orange">
                  mdi-cellphone-arrow-down
                </v-icon>
              </v-list-item-action>
              <v-list-item-content>
                <v-list-item-title>Install Updates</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </slot>
        </v-list>
      </v-menu>
      <!-- </v-toolbar> -->
    </v-app-bar>
    <vue-prompt />
    <v-content
      v-if="$ftUser.username"
    >
      <api-errors :errors="errors" />
      <!-- Content rendered before main-->
      <slot name="top-body-content" />
      <!-- everything else -->
      <v-container
        fluid
        fill-height
        full-width
        :class="[{'pa-0 ma-0': mobileMenu}]"
        :style="wallpaperStyles"
      >
        <v-layout
          align-start
          fill-height
        >
          <v-flex
            align-start
            fill-height
          >
            <v-slide-y-transition
              v-if="!$ftAS.loggingIn"
              mode="out-in"
            >
              <slot />
            </v-slide-y-transition>
            <div v-else>
              <slot name="not-logged-in">
                ...
              </slot>
            </div>
          </v-flex>
        </v-layout>
      </v-container>
    </v-content>
    <v-footer
      app
      :fixed="fixedFooter && !mobileMenu"
      class="px-2"
    >
      <div
        class="ft-footer-company"
      >
        <span>&copy; {{ (new Date()).getFullYear() }} - </span>
        <a
          class="ft-footerlink"
          target="_blank"
          href="http://www.foretees.com"
        >
          <v-avatar
            size="20"
            class="mr-1"
          >
            <img
              :src="footerLogo"
              :alt="appName"
            >
          </v-avatar>
          <span>ForeTees LLC</span>
        </a>
      </div>
      <v-spacer />
      <div
        id="open_intercom"
        style="font-weight: bold; color: #333; padding: 0px 10px 0px 10px; border-radius: 5px; background-color: #f7f7f7; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); display: inline-block; cursor: pointer; margin-right: 10px;"
      >
        ForeTees News
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style="margin-left: 10px; vertical-align: middle;"
        >
          <path
            d="M8 4a5 5 0 0 0-5 5v9H1v2h20a2 2 0 0 0 2-2V9a5 5 0 0 0-5-5zm0 2a3 3 0 0 1 3 3v9H5V9a3 3 0 0 1 3-3m5 7V7h4v2h-2v4z"
            fill="#333"
          />
        </svg>
      </div>
      <server-clock class="ft-footer-clock" />
    </v-footer>
    <v-dialog
      v-model="showHelp"
      :max-width="500"
      scrollable
    >
      <v-card>
        <v-card-title :class="$ftStyle.$ftDialogHeaderClass">
          <v-icon
            large
            class="mr-2"
          >
            fti-logo
          </v-icon>About {{ appName }}
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-card>
            <v-card-text>
              App Version: {{ $ftAPI().appVersion }}
            </v-card-text>
            <v-card-text>
              API Version: {{ $ftAPI().apiVersion() }}
            </v-card-text>
            <v-card-actions v-if="hasIssueTracker()">
              <v-spacer />
              <v-btn
                text
                @click="openIssueTracker"
              >
                <v-icon>mdi-bug</v-icon>Report Issue
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            text
            @click="showHelp = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="showAppSettings"
      :max-width="500"
      scrollable
    >
      <v-card>
        <v-card-title :class="$ftStyle.$ftDialogHeaderClass">
          <v-icon
            large
            class="mr-2"
          >
            mdi-cog-box
          </v-icon>App Settings
        </v-card-title>
        <v-divider />
        <v-card-text>
          <v-select
            v-model="currentAppMode"
            prepend-icon="mdi-theme-light-dark"
            label="App Theme"
            :items="appModes"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            text
            @click="showAppSettings = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <user-select-dialog
      v-if="userLoggedIn"
      :value="showUserSelect"
    />
    <lock-screen
      v-if="$ftAC.lockScreen"
      :value="showLockScreen"
      @unlock="unlockScreen"
    />
    <clock-out-prompt
      v-if="currentTerminalId"
      v-model="showClockOutPrompt"
    />
  </v-app>
</template>

<script>
import VuePrompt from '../plugins/dialogs/VuePrompt'
import LockScreen from './LockScreen'
import UserSelectDialog from './UserSelectDialog'
import ClockOutPrompt from './ClockOutPrompt'
import ServerClock from './ServerClock'
import navRoute from '../mixins/navRoute'
import logoDark from '../images/logo-dark.svg'
import logo from '../images/logo.svg'
import errorHandler from '../mixins/errorHandler'
import wallpaper from '../mixins/wallpaper'
import apiHelpers from '../mixins/itemEditor/mixins/apiHelpers'
import {ftHost} from '../util/helpers'
import {setStyles} from '../store/actions'

var lastActivity = null

export default {
  name: 'App',
  components: {VuePrompt, ServerClock, LockScreen, UserSelectDialog, ClockOutPrompt},
  mixins: [navRoute, errorHandler, apiHelpers, wallpaper],
  props: {
    externalLinks: Array
  },
  data () {
    return {
      showHelp: false,
      showAppSettings: false,
      showClockOutPrompt: false,
      enableIssueCollector: true,
      hideDrawerOverlay: false,
      pinDrawer: false,
      mobileMenu: false,
      condense: false,
      showNavigation: false,
      fixedFooter: true,
      right: true,
      loggingIn: false,
      mobileBreakPoint: 960,
      reservationIssueCount: 0,
      menuAccessUpdateCounter: 0,
      unhookBeforeRoute: () => {},
      openForeTeesTabs: {},
      userActivityTimeout: null,
      userActivityThrottlerTimeout: null,
      screenLockTimer: null,
      appModes: [
        {value: 'light', text: 'Light Theme'},
        {value: 'dark', text: 'Dark Theme'}
      ]
    }
  },
  computed: {
    coreCssVars () {
      return {
        '--ft-warning': this.$ftCurrentTheme.warning
      }
    },
    showClockOut () {
      return this.currentTerminalId && this.$ftUser.clockedIn
    },
    showUserSelect () {
      return this.$ftAS.userLock && !this.$ftAS.screenLocked
    },
    currentTerminalId () {
      return this.$ftAC.terminal && this.$ftAC.terminal.id
    },
    allowAddNewTerminal () {
      return !this.currentTerminalId && this.$ftAccess.registerTerminal
    },
    showLockScreen () {
      return this.currentTerminalId && this.$ftAS.screenLocked
    },
    accountMenuAvatar () {
      // Photo of current user
      return this.$ftUser.photoUrl
    },
    accountMenuIcon () {
      // Icon to use if no photo for user
      return 'mdi-account-circle'
    },
    loadedClubOptions () {
      return !!this.$ftAC.club
    },
    allowAppSettingsOption () {
      return this.appTheme && this.appTheme.enableModeSwitch
    },
    currentAppMode: {
      get () {
        return this.$ftStyle.appMode
      },
      set (v) {
        setStyles(this.$store, {appMode: v})
      }
    },
    appTheme () {
      return this.$ftAC.theme
    },
    appName () {
      return this.$ftAC.name
    },
    hasChildren () {
      return true
    },
    userLoggedIn () {
      return this.$ftUser.apps[this.$ftAPI().appMode()]
    },
    displayUserName () {
      return this.$ftUser.name
        ? this.$ftUser.name.trim() || this.$ftUser.username
        : this.$ftUser.username
    },
    firstMenuNotice () {
      return this.availableRootMenuItems.find(n => n.notice && n.notice[0])
    },
    footerLogo () {
      return this.$ftAppIsDark ? logoDark : logo
    },
    headerLogo () {
      return this.$ftAppIsDark ? this.$ftAC.logoUrlDark || this.$ftAC.logoUrlLight || logoDark : this.$ftAC.logoUrlLight || this.$ftAC.logoUrlDark || logo
    },
    invertLogo () {
      return this.$ftAppIsDark && !this.$ftAC.logoUrlDark && this.$ftAC.logoUrlLight || !this.$ftAppIsDark && !this.$ftAC.logoUrlLight && this.$ftAC.logoUrlDark
    },
    hasNotifications () {
      return this.$ftAS.updateAvailable
    },
    notificationIcon () {
      return this.$ftAS.updateAvailable ? 'mdi-download' : 'mdi-bullhorn'
    },
    notificationColor () {
      return this.$ftAS.updateAvailable ? 'orange' : 'blue'
    },
    logoutIcon () {
      return this.currentTerminalId ? 'mdi-power' : 'mdi-exit-to-app'
    },
    logoutText () {
      return this.currentTerminalId ? 'Shut Down Terminal' : 'Logout'
    },
    clockOutIcon () {
      return 'mdi-clock-out'
    },
    clockOutText () {
      return 'Clock Out'
    },
    navigationDrawerState: {
      get () {
        return this.mobileMenu ? this.showNavigation : this.hasActiveSubMenus
      },
      set (val) {
        this.showNavigation = val
      }
    },
    menuWidth () {
      return this.mobileMenu ? 280 : 250
    }
  },
  watch: {
    menuAccessUpdateCounter () {
      console.log('Detected signal to rebuild menu access -- updating Nav, check page access', this.$ftCS)
      this.runNavItemUpdaters()
      this.checkPageAccess()
    },
    coreMenuItems: {
      handler () {
        console.warn('Menu data changed -- re-generating Nav', this.coreMenuItems)
        this.setNavItems()
      },
      deep: true
    },
    $ftCS: {
      handler () {
        console.log('Club Settings changed -- signaling menu access update', this.$ftCS)
        this.menuAccessUpdateCounter ++
      },
      deep: true
    },
    appTheme: {
      handler () {
        console.warn('Theme settings changed', this.appTheme)
        this.updateTheme()
      },
      deep: true
    },
    $ftUser: {
      handler () {
        console.log('User data changed -- signaling menu access update', this.$ftUser)
        this.menuAccessUpdateCounter ++
      },
      deep: true
    },
    $ftAppIsDark (v) {
      if (this.$vuetify.theme.dark === v) return
      this.$vuetify.theme.dark = v
    },
    $route (route) {
      console.warn('Route Changed', route)
      this.onEachRoute()
      if (!this.$ftAPI().checkSkLogin()) {
        // We're not processing a suak
        // this.checkLogin()
        this.$ftAPI().checkUser()
        this.runNavItemUpdaters() // TODO: remove need to call this on every route change
        this.$emit('route-change', route)
        this.checkPageAccess()
        this.$ftAPI().checkVersion()
      } else {
        console.warn('Logging in via SUAK')
      }
    }
  },
  created () {
    console.warn('Created CoreNavigation -- generating Nav', this.coreMenuItems)
    this.setNavItems()
  },
  mounted () {
    this.updateTheme()
    this.onResize()
    setTimeout(() => this.showNavigation = true, 0)
    setTimeout(() => this.showNavigation = false, 1000)
    this.periodicUserCheck()
    this.onEachRoute()
    window.addEventListener('storage', this.storageEventHandler)
    // Make sure global and local vuex appState match
    const globalState = this.$ftAPI().getGlobalAppState(true)
    this.$ftAPI().setGlobalAppState(globalState) // setGlobalAppState will also update vuex
    // Hook all navigation, before it happens
    this.unhookBeforeRoute = this.$router.beforeEach((to, from, next) => {
      if (this.$ftAS.userLock) {
        // do nothing.  we don't want to route if we're locked
        console.warn('Cancelled navigation due to userLock', to, from)
        next(false)
      } else {
        next()
      }
    })
    window.addEventListener('mousemove', this.userActivityThrottler)
    window.addEventListener('scroll', this.userActivityThrottler)
    window.addEventListener('keydown', this.userActivityThrottler)
    window.addEventListener('resize', this.userActivityThrottler)
    window.addEventListener('click', this.userActivityThrottler)
    window.addEventListener('touchstart', this.userActivityThrottler)
    this.$ftAPI().setGlobalAppState({lastActivity: Date.now()})
    this.screenLockTimer = setInterval(() => {
      const minDate = Math.max(this.$ftAC.lockScreenTimeout, 1) * 1000 /* 10000 */
      if (Date.now() - this.$ftAS.lastActivity > minDate && Date.now() - lastActivity > minDate) {
        this.$ftAPI().checkRecordLocks()
        if (!this.$ftAS.allowScreenLock) {
          console.error('No allow screen lock', this.$ftAPI().openRecordLocks(), this.$ftAPI().getGlobalAppState())
        }
        if (this.$ftAC.lockScreenTimeout && this.$ftAC.lockScreen && !this.$ftAS.screenLocked && this.$ftAS.allowScreenLock) {
          console.log('User inactive')
          this.lockScreen()
        }
      }
    }, 1000)
  },
  beforeDestroy () {
    window.removeEventListener('storage', this.storageEventHandler)
    window.removeEventListener('mousemove', this.userActivityThrottler)
    window.removeEventListener('scroll', this.userActivityThrottler)
    window.removeEventListener('keydown', this.userActivityThrottler)
    window.removeEventListener('resize', this.userActivityThrottler)
    window.removeEventListener('click', this.userActivityThrottler)
    window.removeEventListener('touchstart', this.userActivityThrottler)
    if (this.screenLockTimer) clearInterval(this.screenLockTimer)
    this.unhookBeforeRoute()
  },
  beforeMount () {
    console.log('App query:', this.$route.query)
    this.$ftAPI().checkSkLogin()
  },
  methods: {
    storageEventHandler (e) {
      // let ftAPI sync any global storage events
      this.$ftAPI().storageEvent(event)
    },
    userActivityThrottler () {
      lastActivity = Date.now()
      if (!this.userActivityThrottlerTimeout) {
        this.userActivityThrottlerTimeout = setTimeout(() => {
          // console.log('Detected User Activity')
          this.$ftAPI().setGlobalAppState({lastActivity: Date.now()})
          clearTimeout(this.userActivityThrottlerTimeout)
          this.userActivityThrottlerTimeout = null
        }, 1000)
      }
    },
    unlockScreen () {
      this.$ftAPI().setGlobalAppState({screenLocked: false})
    },
    lockScreen () {
      if (this.$ftAS.allowScreenLock) this.$ftAPI().lockUserAndScreen()
    },
    onEachRoute () {
      this.showWallpaper = !!this.$router.currentRoute.meta.showBg
    },
    updateTheme () {
      const theme = this.appTheme
      console.warn('Updating theme', theme)
      if (!theme) return
      if (!this.allowAppSettingsOption) {
        this.currentAppMode = theme.defaultMode
      }
      const customCssId = 'ft-club-css'
      var el = document.getElementById(customCssId)
      if (theme.customCss) {
        if (!el) {
          el = document.createElement('style')
          el.setAttribute('id', customCssId)
          el.setAttribute('type', 'text/css')
          document.head.appendChild(el)
        }
        el.innerHTML = theme.customCss
        console.warn('Setting custom css')
      } else if (el) {
        el.remove()
        console.warn('Removing custom css')
      }
      this.$vuetify.theme.dark = this.$ftAppIsDark
      if (theme.lightThemePrimary) this.$vuetify.theme.themes.light.primary = theme.lightThemePrimary
      if (theme.lightThemeSecondary) this.$vuetify.theme.themes.light.secondary = theme.lightThemeSecondary
      if (theme.lightThemeTertiary) this.$vuetify.theme.themes.light.tertiary = theme.lightThemeTertiary
      if (theme.lightThemeAccent) this.$vuetify.theme.themes.light.accent = theme.lightThemeAccent
      if (theme.lightThemeSecondaryAccent) this.$vuetify.theme.themes.light['secondary-accent'] = theme.lightThemeSecondaryAccent
      if (theme.lightThemeTertiaryAccent) this.$vuetify.theme.themes.light['tertiary-accent'] = theme.lightThemeTertiaryAccent
      if (theme.lightThemeError) this.$vuetify.theme.themes.light.error = theme.lightThemeError
      if (theme.lightThemeInfo) this.$vuetify.theme.themes.light.info = theme.lightThemeInfo
      if (theme.lightThemeSuccess) this.$vuetify.theme.themes.light.success = theme.lightThemeSuccess
      if (theme.lightThemeWarning) this.$vuetify.theme.themes.light.warning = theme.lightThemeWarning
      if (theme.lightThemeComplimentOrange) this.$vuetify.theme.themes.light['compliment-orange'] = theme.lightThemeComplimentOrange
      if (theme.lightThemeComplimentOrangeAccent) this.$vuetify.theme.themes.light['compliment-orange-accent'] = theme.lightThemeComplimentOrangeAccent
      if (theme.lightThemeComplimentPurple) this.$vuetify.theme.themes.light['compliment-purple'] = theme.lightThemeComplimentPurple
      if (theme.lightThemeComplimentPurpleAccent) this.$vuetify.theme.themes.light['compliment-purple-accent'] = theme.lightThemeComplimentPurpleAccent
      if (theme.lightThemeComplimentYellow) this.$vuetify.theme.themes.light['compliment-yellow'] = theme.lightThemeComplimentYellow
      if (theme.lightThemeComplimentYellowAccent) this.$vuetify.theme.themes.light['compliment-yellow-accent'] = theme.lightThemeComplimentYellowAccent
      if (theme.lightThemeComplimentRed) this.$vuetify.theme.themes.light['compliment-red'] = theme.lightThemeComplimentRed
      if (theme.lightThemeComplimentRedAccent) this.$vuetify.theme.themes.light['compliment-red-accent'] = theme.lightThemeComplimentRedAccent
      if (theme.lightThemeComplimentGreen) this.$vuetify.theme.themes.light['compliment-green'] = theme.lightThemeComplimentGreen
      if (theme.lightThemeComplimentGreenAccent) this.$vuetify.theme.themes.light['compliment-green-accent'] = theme.lightThemeComplimentGreenAccent


      if (theme.darkThemePrimary) this.$vuetify.theme.themes.dark.primary = theme.darkThemePrimary
      if (theme.darkThemeSecondary) this.$vuetify.theme.themes.dark.secondary = theme.darkThemeSecondary
      if (theme.darkThemeTertiary) this.$vuetify.theme.themes.dark.tertiary = theme.darkThemeTertiary
      if (theme.darkThemeAccent) this.$vuetify.theme.themes.dark.accent = theme.darkThemeAccent
      if (theme.darkThemeSecondaryAccent) this.$vuetify.theme.themes.dark['secondary-accent'] = theme.darkThemeSecondaryAccent
      if (theme.darkThemeTertiaryAccent) this.$vuetify.theme.themes.dark['tertiary-accent'] = theme.darkThemeTertiaryAccent
      if (theme.darkThemeError) this.$vuetify.theme.themes.dark.error = theme.darkThemeError
      if (theme.darkThemeInfo) this.$vuetify.theme.themes.dark.info = theme.darkThemeInfo
      if (theme.darkThemeSuccess) this.$vuetify.theme.themes.dark.success = theme.darkThemeSuccess
      if (theme.darkThemeWarning) this.$vuetify.theme.themes.dark.warning = theme.darkThemeWarning
      if (theme.darkThemeComplimentOrange) this.$vuetify.theme.themes.dark['compliment-orange'] = theme.darkThemeComplimentOrange
      if (theme.darkThemeComplimentOrangeAccent) this.$vuetify.theme.themes.dark['compliment-orange-accent'] = theme.darkThemeComplimentOrangeAccent
      if (theme.darkThemeComplimentPurple) this.$vuetify.theme.themes.dark['compliment-purple'] = theme.darkThemeComplimentPurple
      if (theme.darkThemeComplimentPurpleAccent) this.$vuetify.theme.themes.dark['compliment-purple-accent'] = theme.darkThemeComplimentPurpleAccent
      if (theme.darkThemeComplimentYellow) this.$vuetify.theme.themes.dark['compliment-yellow'] = theme.darkThemeComplimentYellow
      if (theme.darkThemeComplimentYellowAccent) this.$vuetify.theme.themes.dark['compliment-yellow-accent'] = theme.darkThemeComplimentYellowAccent
      if (theme.darkThemeComplimentRed) this.$vuetify.theme.themes.dark['compliment-red'] = theme.darkThemeComplimentRed
      if (theme.darkThemeComplimentRedAccent) this.$vuetify.theme.themes.dark['compliment-red-accent'] = theme.darkThemeComplimentRedAccent
      if (theme.darkThemeComplimentGreen) this.$vuetify.theme.themes.dark['compliment-green'] = theme.darkThemeComplimentGreen
      if (theme.darkThemeComplimentGreenAccent) this.$vuetify.theme.themes.dark['compliment-green-accent'] = theme.darkThemeComplimentGreenAccent


      const backgroundGrey = ['backgroundGray', 'backgroundGrey'].find(e => !!theme[e])
      if (backgroundGrey) {
        ['background-grey', 'background-gray'].forEach(e => {
          this.$vuetify.theme.themes.dark[e] = theme[backgroundGrey]
          this.$vuetify.theme.themes.light[e] = theme[backgroundGrey]
        })
      }
      if (theme.backgroundBlue) {
        const e = 'background-blue'
        this.$vuetify.theme.themes.dark[e] = theme.backgroundBlue
        this.$vuetify.theme.themes.light[e] = theme.backgroundBlue
      }
      if (theme.backgroundBlueLight) {
        const e = 'background-blue-light'
        this.$vuetify.theme.themes.dark[e] = theme.backgroundBlueLight
        this.$vuetify.theme.themes.light[e] = theme.backgroundBlueLight
      }
      console.warn('Updated theme', this.$vuetify)
    },
    navGroupClick (ref, item, $event) {
      const group = this.$refs[ref] && this.$refs[ref][0]
      if (!this.mobileMenu) {
        if (item.to && !item.activePath) {
          this.$router.push(item.to)
        }
        const reactivate = () => {group.isActive = true}
        setTimeout(reactivate, 0)
        setTimeout(reactivate, 100)
      }
    },
    closeDrawer () {
      this.showNavigation = false
    },
    menuItemClickAction (navItem) {
      if (navItem && navItem.linkAction) {
        navItem.linkAction(this)
      }
      this.showNavigation = this.mobileMenu ? false : this.showNavigation
    },
    toggleDrawer () {
      this.showNavigation = !this.showNavigation
    },
    logout () {
      this.$ftAPI().logoutPrompt()
    },
    clockOut () {
      this.showClockOutPrompt = true
    },
    foreTeesLinkBuilder (parms) {
      return ftHost() + '/v5/servlet/Login' + parms.reduce((acc, o) => {
        acc += (acc ? '&' : '?') + encodeURIComponent(o.parm) + (o.value === null || o.value === undefined ? '' : '=' + encodeURIComponent(o.value))
        return acc
      }, '')
    },
    externalLink (link) {
      const parms = (link.parms || []).concat(!link.url ? [{parm: 'aspro', value: null}] : [])
      if (!link.url) {
        // eslint-disable-next-line no-constant-condition
        if (link.id === 'open_intercom') {
          return
        }
        const baseWindowName = this.foreTeesLinkBuilder(parms)
        var ftBrowserTab = this.openForeTeesTabs[baseWindowName]
        if (!ftBrowserTab || ftBrowserTab.closed) {
          ftBrowserTab = window.open('', baseWindowName)
          this.openForeTeesTabs[baseWindowName] = ftBrowserTab
          // Get a Single Use Access Key (SUAK) using our current JWT
          // (Vue clients use JWT to authentication each request, not JSESSION_IDs)
          // Since we'll be authenticating with a value visible in the browser's address bar, we'll use a SUAK
          // so it can only be used for authenticating once.
          // (SUAKS are only usable for a short period of time, in addition to only being useable once, so they must be used immediately.)
          this.$ftAPI().get(this.addResourceErrorHandler([this.singleUseAccessKeyCommand((suak) => {
            // Success
            console.log('Got SUAK:', suak)
            parms.push({parm: 'sk', value: suak})
            const url = this.foreTeesLinkBuilder(parms)
            console.log('Opening foretees link: ', url)
            // Open link into foretees application using Single Use Access Key to create a valid java session.
            ftBrowserTab.location = url
          })], (response) => {
            // Error getting SUAK
            console.log('Error from SUAK', response)
            ftBrowserTab.close()
          }))
        } else {
          ftBrowserTab.focus()
        }
      } else {
        window.open('', link.url)
      }

    },
    singleUseAccessKeyCommand (onLoad) {
      return {
        command: 'singleUseKey',
        load: onLoad,
        loading: this.setIsLoading
      }
    },
    onResize () {
      // console.log('App Vue', this)
      this.condense = window.innerWidth < 1200
      this.fixedFooter = window.innerHeight > 700
      const wasMobile = this.mobileMenu
      this.mobileMenu = window.innerWidth < this.mobileBreakPoint
      if (!wasMobile && this.mobileMenu) {
        this.showNavigation = false // hide menu when switching to mobile
      }
      // console.log('Window Width', window.innerWidth)
    },
    showAppUpdateMessage () {
      this.$vPrompt({
        title: 'Update Available',
        titleIcon: 'mdi-download',
        titleIconColor: 'success',
        messages: [
          'Version ' + this.$ftAS.versionAvailable + ' of ' + this.appName + ' is available.',
          'Update now?'
        ],
        confirmText: 'Update',
        modal: true,
        confirmAction: () => {
          location.reload()
        },
        cancelAction: true
      })
    },
    periodicUserCheck () {
      setTimeout(() => {
        if (this.userLoggedIn) {
          this.$ftAPI().checkUser(true) // Check user, but do not renew token
        }
        this.periodicUserCheck() // And do it again in ~5 min.
      }, 300000)
    },
    hasIssueTracker () {
      return !!window.__ATL_showIssueCollectorDialog
    },
    openIssueTracker () {
      if (this.hasIssueTracker()) {
        window.__ATL_showIssueCollectorDialog()
      }
    },
    addNewTerminal () {
      this.$router.push('/configuration/terminals?sxp=1&showId')
    },
    userSelect () {
      this.$ftAPI().setGlobalAppState({userLock: true})
    }
  }
}
</script>

<style>
.ft-navigation-drawer .v-list-item__icon.v-list-group__header__append-icon {
  margin-left: 0 !important;
}
.ft-footer-company {
  font-size: 90%;
}
.ft-invert {
   -webkit-filter: invert(100%);
          filter: invert(100%);
}
.badge-nav-item {
  position: fixed;
  transform: translateX(16px);
}
</style>
